/**
 * The admin console.
 *
 * ── What it can see ─────────────────────────────────────────────────────────
 *
 * **Every account, from the database.** It used to read `localStorage`, which
 * was correct while that was where accounts lived — but the record is a row in
 * Postgres now, so the console could only see accounts that had signed in on
 * this machine, and every edit it made was overwritten by the next load. It was
 * a panel of buttons that did nothing.
 *
 * That is also why testing with two accounts was so awkward: two browsers, and
 * each console could only ever see itself.
 *
 * ── Permission is not decided here ──────────────────────────────────────────
 *
 * This draws itself for anyone the client-side `isAdmin` accepts, which reads an
 * *unverified* token and is therefore worth nothing. Every endpoint behind these
 * buttons checks the verified token's role and answers 403 otherwise. The gate
 * on this component is about not showing a menu nobody can use.
 *
 * The starmap behind it is the real component in backdrop mode rather than a
 * copy. A second eight-hundred-line starmap kept in step with the first would be
 * a liability, and the marker work already lives there.
 */

import {
  DEFAULT_LINE_COLOR,
  COMPONENT_SPECS,
  COMPONENT_TYPES,
  WARP_OVERDRIVE_CORE,
  formatLinelits,
  getSystem,
  type CargoItem,
} from '@interspacial/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PlayerRecord } from '@interspacial/shared';

import { Starmap } from './Starmap';
import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';
import {
  clearHold,
  fetchPlayers,
  readPlayer,
  givePlayer,
  placePlayer,
  resetPlayer,
  setStack,
  setWallet,
  type AdminPlayer,
} from '../net/admin';
import { RefusedError } from '../net/account';

export interface AdminViewProps {
  /** The signed-in admin, so their own account can be flagged in the list. */
  uuid: string;
  /** Their game token. Every call here is authorised server-side from it. */
  token: string;
  /** The world seed, for naming the system an account is sitting in. */
  seed: string;
  onExit: () => void;
  /** Reload the live game state after editing the account being played. */
  onReloadSelf: () => void;
}

/**
 * What an admin can conjure into a hold.
 *
 * Every component plus the things that are not components — a joint, some
 * line, and the overdrive core, which is the one you actually want when
 * testing travel and the one the game is least likely to hand you on demand.
 */
const GIFTS: { id: string; label: string; item: Omit<CargoItem, 'count'> }[] = [
  {
    id: WARP_OVERDRIVE_CORE.id,
    label: WARP_OVERDRIVE_CORE.label,
    item: {
      id: WARP_OVERDRIVE_CORE.id,
      kind: 'resource',
      label: WARP_OVERDRIVE_CORE.label,
      weight: WARP_OVERDRIVE_CORE.weight,
    },
  },
  { id: 'joint', label: 'joint', item: { id: 'joint', kind: 'joint', label: 'joint' } },
  ...[10, 25, 50].map((length) => ({
    id: `line-${length}`,
    label: `${length}u line`,
    item: {
      id: `line-${length}-1`,
      kind: 'line' as const,
      label: `${length}u line`,
      length,
      strength: 1,
      color: DEFAULT_LINE_COLOR,
    },
  })),
  ...COMPONENT_TYPES.map((type) => ({
    id: `part-${type}`,
    label: COMPONENT_SPECS[type].name,
    item: { id: `component-${type}`, kind: 'component' as const, label: COMPONENT_SPECS[type].name, type },
  })),
];

export function AdminView({ uuid, token, seed, onExit, onReloadSelf }: AdminViewProps) {
  const [accounts, setAccounts] = useState<AdminPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(uuid);
  const [note, setNote] = useState<string | null>(null);
  /** Which account is one click from being wiped. */
  const [confirming, setConfirming] = useState<string | null>(null);
  /** What the give control will hand over next. */
  const [giving, setGiving] = useState<string>(GIFTS[0]!.id);
  /**
   * The selected account's full record, which the list rows do not carry.
   *
   * The list is a summary — a hold of forty stacks per row would make listing
   * every player expensive for a panel that shows one at a time. Every edit
   * answers with the whole record, so this is filled by acting as much as by
   * selecting.
   */
  const [detail, setDetail] = useState<PlayerRecord | null>(null);

  const account = accounts.find((a) => a.uuid === selected) ?? null;

  /**
   * Anything that touches the server, with its refusal shown rather than thrown.
   *
   * "You are not an administrator" and "no such player" are sentences for the
   * screen. Everything else is a bug and goes to the console, where a stack
   * trace is worth having.
   */
  const attempt = useCallback(
    async (what: string, run: () => Promise<void>) => {
      try {
        await run();
      } catch (error) {
        if (error instanceof RefusedError) {
          setNote(error.message);
          return;
        }
        console.error(`[admin] ${what} failed`, error);
        setNote(`${what} failed - see the console.`);
      }
    },
    [],
  );

  const reload = useCallback(
    (message?: string) =>
      attempt('loading players', async () => {
        const players = await fetchPlayers(token);
        setAccounts(players);
        setLoading(false);
        if (message) setNote(message);
      }),
    [attempt, token],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Take the record an edit answered with.
   *
   * The list is refreshed too, because a balance or a berth shown in a row has
   * just moved. The game above is holding its own copy of the account being
   * played, and if that is the one edited it has just gone stale.
   */
  const took = (record: PlayerRecord, message: string) => {
    setDetail(record);
    if (record.uuid === uuid) onReloadSelf();
    void reload(message);
  };

  /** Pull the whole record for a row. The list rows only carry a summary. */
  const inspect = (target: string) => {
    setSelected(target);
    setNote(null);
    setConfirming(null);
    setDetail(null);

    void attempt('reading the account', async () => {
      setDetail(await readPlayer(token, target));
    });
  };

  useEffect(() => {
    if (selected && !detail) inspect(selected);
    // Only when the selection changes: `inspect` sets state this would react to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /**
   * Add one of something to the selected hold.
   *
   * Capacity is deliberately ignored, server-side as well as here. An admin
   * handing out a test item should not be stopped by a vault.
   */
  const give = (id: string) => {
    const gift = GIFTS.find((option) => option.id === id);
    if (!gift || !account) return;

    void attempt('giving', async () => {
      const record = await givePlayer(token, account.uuid, { ...gift.item, count: 1 });
      took(record, `Gave ${gift.label}.`);
    });
  };

  /*
   * Where the account is.
   *
   * Always a berth: there is no mid-flight resume, so a stored player is a
   * docked player. The old console showed a live flight position because the
   * browser was keeping one; the database never has.
   */
  const position = useMemo(() => {
    if (!account?.systemId) return null;

    const system = getSystem(seed, account.systemId);
    const station = account.stationId
      ? system.stations.find((s) => s.id === account.stationId)
      : null;

    return {
      system: system.name,
      detail: station ? `docked at ${station.name}` : 'no berth assigned',
    };
  }, [account, seed]);

  /**
   * Where to send somebody.
   *
   * Two lists, and the first is the point of the tool now: **where everybody
   * else is**. Getting two accounts into the same room used to mean reading one
   * player's system off the table, finding it in the second list, and hoping it
   * was the right one — so the console now offers the players directly and works
   * the system out itself.
   *
   * A destination with no station is a real destination. Three systems in ten
   * have none, and somebody sitting in one of those is precisely the person
   * worth joining — refusing to list them would have made the accounts this is
   * for the ones it could not reach. They arrive in flight, which is the state
   * the client already resumes a berthless player into.
   *
   * The second list is every station in the admin's own system, which is what
   * this was before and still the right answer for "put them next to me".
   */
  const berths = useMemo(() => {
    const options: {
      key: string;
      group: 'players' | 'here';
      systemId: string;
      stationId: string | null;
      label: string;
    }[] = [];

    /*
     * One entry per other account, at whichever station they are docked.
     *
     * Not deduplicated by system: two accounts in one system are two people to
     * join, and the row says who as well as where. An account with no system at
     * all has never been loaded, and there is nowhere to send anybody.
     */
    for (const other of accounts) {
      if (other.uuid === account?.uuid) continue;
      if (!other.systemId) continue;

      let system;
      try {
        system = getSystem(seed, other.systemId);
      } catch {
        // A record from an older galaxy. Skipped rather than crashing the
        // console, which is the one screen that has to keep working.
        continue;
      }

      const docked = other.stationId
        ? system.stations.find((one) => one.id === other.stationId)
        : null;

      // Docked where they are; otherwise any berth in the system, so arriving
      // lands somewhere rather than adrift. No station at all is left null and
      // the arrival is a flight, which is what the system genuinely offers.
      const at = docked ?? system.stations[0] ?? null;

      options.push({
        key: `player:${other.uuid}`,
        group: 'players',
        systemId: other.systemId,
        stationId: at?.id ?? null,
        label:
          `${other.displayId} — ${system.name}` +
          (docked ? ` · ${docked.name}` : at ? ` · ${at.name} (they are flying)` : ' · no station'),
      });
    }

    const here = accounts.find((a) => a.uuid === uuid)?.systemId;
    if (here) {
      let system;
      try {
        system = getSystem(seed, here);
        for (const station of system.stations) {
          options.push({
            key: `here:${station.id}`,
            group: 'here',
            systemId: here,
            stationId: station.id,
            label: `${system.name} - ${station.name}`,
          });
        }
      } catch {
        // As above.
      }
    }

    return options;
  }, [accounts, seed, uuid, account?.uuid]);

  return (
    <>
      {/* The galaxy, centred on wherever the selected account is docked.
          No ship marker: a stored player is always at a berth, never adrift,
          so there is no flight position to draw. */}
      <Starmap
        seed={seed}
        currentSystemId={account?.systemId ?? undefined}
        ship={null}
        asBackdrop
        onSeedChange={() => undefined}
        onEnterSystem={() => undefined}
      />

      <div style={{ ...PANEL, top: 16, left: 16, width: 300, maxHeight: '60vh', overflowY: 'auto' }}>
        <Header>accounts</Header>

        <div style={{ ...NOTE, marginBottom: 8 }}>
          Every account on the server, newest first.
        </div>

        {loading && <div style={NOTE}>Loading...</div>}
        {!loading && accounts.length === 0 && <div style={NOTE}>Nobody has played yet.</div>}

        {accounts.map((entry) => (
          <button
            key={entry.uuid}
            type="button"
            onClick={() => inspect(entry.uuid)}
            style={{
              ...ROW,
              background: entry.uuid === selected ? 'rgba(255,138,61,0.18)' : 'transparent',
              borderColor: entry.uuid === selected ? HUD_ORANGE : HUD_EDGE,
            }}
          >
            <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {entry.username || entry.uuid}
              {entry.displayId && (
                <span style={{ color: HUD_DIM }}> · {entry.displayId}</span>
              )}
              {entry.uuid === uuid && <span style={{ color: HUD_ORANGE }}> - you</span>}
            </span>
            <span style={{ color: HUD_DIM, fontSize: 10 }}>
              {entry.onboarded ? 'played' : 'new'}
            </span>
          </button>
        ))}
      </div>

      {account && (
        <div style={{ ...PANEL, top: 16, right: 16, width: 330, maxHeight: '78vh', overflowY: 'auto' }}>
          {/* The person, then the hull they fly. Both, because an admin
              looking for somebody may have either one to go on. */}
          <Header>
            {account.uuid === uuid ? 'you' : account.username || account.uuid}
            {account.displayId ? ` · ${account.displayId}` : ''}
          </Header>

          {position && (
            <div style={{ marginBottom: 10 }}>
              <Field label="system" value={position.system} />
              <Field label="berth" value={position.detail} />
              {/* The step, not a guess from `onboarded` — that only turns true
                  on the final docking, so a player halfway through the approach
                  read as "not started" and there was no way to tell a wipe that
                  had worked from one that had not. */}
              <Field
                label="tutorial"
                value={
                  account.onboarded
                    ? 'complete'
                    : account.tutorial
                      ? `at ${account.tutorial}`
                      : 'not started'
                }
              />
            </div>
          )}

          {/* Where to put somebody.
              The reason this exists: two test accounts that started in different
              systems are a long flight apart, and the alternative was granting an
              overdrive core and flying it. It lists the other accounts by where
              they are, so joining somebody is one choice rather than a lookup.

              They arrive docked where there is a berth and in flight where there
              is not — see `place`. A system with no station is a real place to
              be, and the client already resumes a berthless player into the
              cockpit. */}
          {berths.length > 0 && (
            <>
              <Header>send to</Header>
              <select
                defaultValue=""
                onChange={(event) => {
                  // Keyed by the option's own key rather than by its station,
                  // because a station is no longer unique among these — and for
                  // a system with none there is nothing to key on at all.
                  const berth = berths.find((option) => option.key === event.target.value);
                  if (!berth) return;
                  void attempt('moving', async () => {
                    const record = await placePlayer(token, account.uuid, {
                      systemId: berth.systemId,
                      stationId: berth.stationId,
                    });
                    took(record, `Moved to ${berth.label}.`);
                  });
                }}
                style={{ ...INPUT, marginTop: 0, marginBottom: 10 }}
              >
                <option value="">pick a destination...</option>

                {/* Players first. Joining somebody is what this is for; picking
                    a station off a list is the fallback. */}
                {berths.some((option) => option.group === 'players') && (
                  <optgroup label="join a player">
                    {berths
                      .filter((option) => option.group === 'players')
                      .map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                  </optgroup>
                )}

                {berths.some((option) => option.group === 'here') && (
                  <optgroup label="stations in my system">
                    {berths
                      .filter((option) => option.group === 'here')
                      .map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </>
          )}

          <Header>linelits</Header>
          {!detail && <div style={NOTE}>Reading the account...</div>}
          {detail && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {(['carried', 'banked'] as const).map((pile) => (
                <label key={pile} style={{ flex: 1, fontSize: 11, color: HUD_DIM }}>
                  {pile}
                  <input
                    type="number"
                    defaultValue={detail.wallet[pile]}
                    /* On blur rather than on every keystroke. This is a request
                       now, and one per digit typed would be a request per digit
                       typed — each answering with a record that resets the field
                       under the cursor. */
                    onBlur={(event) => {
                      const value = Math.max(0, Number(event.target.value) || 0);
                      if (value === detail.wallet[pile]) return;
                      void attempt('setting the purse', async () => {
                        const record = await setWallet(token, account.uuid, { [pile]: value });
                        took(record, 'Purse set.');
                      });
                    }}
                    style={INPUT}
                  />
                </label>
              ))}
            </div>
          )}

          <Header>cargo · {detail?.cargo.length ?? 0} stacks</Header>
          <div style={{ marginBottom: 10 }}>
            {detail?.cargo.length === 0 && <div style={NOTE}>Empty.</div>}
            {detail?.cargo.map((item: CargoItem) => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                <input
                  type="number"
                  defaultValue={item.count}
                  onBlur={(event) => {
                    const count = Math.max(0, Number(event.target.value) || 0);
                    if (count === item.count) return;
                    void attempt('setting a stack', async () => {
                      const record = await setStack(token, account.uuid, item.id, count);
                      took(record, 'Hold updated.');
                    });
                  }}
                  style={{ ...INPUT, width: 62, marginTop: 0 }}
                />
              </div>
            ))}
            {/* Putting things *in*, which this could not do at all.
                Editing a count only worked on a stack that already existed, so
                testing anything the player did not already own — an overdrive
                core, most obviously — meant playing until one turned up. */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <select
                value={giving}
                onChange={(event) => setGiving(event.target.value)}
                style={{ ...INPUT, flex: 1, marginTop: 0 }}
              >
                {GIFTS.map((gift) => (
                  <option key={gift.id} value={gift.id}>
                    {gift.label}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => give(giving)} style={{ ...ACTION, width: 54 }}>
                +1
              </button>
            </div>

            {(detail?.cargo.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={() =>
                  void attempt('emptying the hold', async () => {
                    const record = await clearHold(token, account.uuid);
                    took(record, 'Hold emptied.');
                  })
                }
                style={{ ...ACTION, marginTop: 6 }}
              >
                empty the hold
              </button>
            )}
          </div>

          <Header>reset</Header>

          {/* Replaying the tutorial *is* a reset.
              The introduction puts you in a chosen system a thousand units off
              a station in a fresh starter — none of which is true of an account
              that has been playing. Keeping the ship and re-running the lesson
              would have Control talking you through an approach you are not on,
              in a hull you did not spawn with. So it is one button, and it says
              what it does. */}
          <div style={{ ...NOTE, marginBottom: 6 }}>
            Replaying the tutorial resets the account: ship, purse, hold and progress all go. The
            introduction only makes sense from a standing start.
          </div>

          {confirming === account.uuid ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() =>
                  void attempt('resetting', async () => {
                    await resetPlayer(token, account.uuid);
                    setConfirming(null);
                    setDetail(null);
                    setSelected(null);
                    if (account.uuid === uuid) onReloadSelf();
                    void reload('Reset. It will start the tutorial next time.');
                  })
                }
                style={{ ...ACTION, borderColor: '#ff5f6b', color: '#ff5f6b' }}
              >
                yes, wipe it
              </button>
              <button type="button" onClick={() => setConfirming(null)} style={ACTION}>
                cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(account.uuid)}
              style={{ ...ACTION, borderColor: '#ff5f6b', color: '#ff5f6b' }}
            >
              reset and replay tutorial
            </button>
          )}

          {detail && (
            <div style={{ ...NOTE, marginTop: 10 }}>
              purse {formatLinelits(detail.wallet.carried)}L carried,{' '}
              {formatLinelits(detail.wallet.banked)}L banked
            </div>
          )}

          {note && <div style={{ ...NOTE, color: '#ffb347', marginTop: 8 }}>{note}</div>}
        </div>
      )}

      <button type="button" onClick={onExit} style={{ ...ACTION, position: 'absolute', bottom: 16, left: 16, width: 'auto', padding: '8px 18px' }}>
        ← back
      </button>
    </>
  );
}

function Header({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: HUD_ORANGE,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        marginBottom: 5,
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
      <span style={{ color: HUD_DIM }}>{label}</span>
      <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </div>
  );
}

const PANEL: React.CSSProperties = {
  position: 'absolute',
  background: HUD_BACKING,
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  padding: '12px 14px',
  fontSize: 12,
};

const ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '5px 8px',
  marginBottom: 3,
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  font: 'inherit',
  fontSize: 11.5,
  cursor: 'pointer',
};

const INPUT: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 3,
  padding: '4px 6px',
  background: 'rgba(0,0,0,0.4)',
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  font: 'inherit',
  fontSize: 12,
};

const ACTION: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  background: 'rgba(20,12,7,0.7)',
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_TEXT,
  font: 'inherit',
  fontSize: 11.5,
  cursor: 'pointer',
};

const NOTE: React.CSSProperties = {
  fontSize: 10.5,
  color: HUD_DIM,
  lineHeight: 1.45,
};
